import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
} from '@mui/material';

function About() {
  const { t } = useTranslation();

  const info = [
    { label: t('about.location'), value: 'Paris, France' },
    { label: t('about.experience'), value: '3+' },
  ];

  const languages = [
    { code: 'FR', name: 'Français', level: 'Natif', color: '#ff6b6b' },
    { code: 'EN', name: 'English', level: 'TOEIC 930', color: '#4ecdc4' },
    { code: 'JP', name: '日本語', level: 'JLPT N4', color: '#ffd93d' },
    { code: 'DE', name: 'Deutsch', level: 'A2', color: '#ff8787' },
  ];

  return (
    <Box
      id="about"
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        width: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.75) 0%, rgba(10, 10, 10, 0.85) 100%)',
          backdropFilter: 'blur(20px)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 500,
              mb: 1.5,
              color: '#ffffff',
              fontFamily: '"Excellence In Motion", sans-serif',
            }}
          >
            {t('about.title')}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.0625rem', md: '1.125rem' },
                color: '#d4d4d4',
                lineHeight: 2,
                mb: 4,
                fontWeight: 400,
              }}
            >
              {t('about.description')}
            </Typography>

            <Paper
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15), rgba(78, 205, 196, 0.1))',
                border: '2px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden',
                backdropFilter: 'blur(10px)',
                maxWidth: '100%',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#ffffff',
                  fontSize: { xs: '0.875rem', sm: '0.95rem' },
                  lineHeight: 1.9,
                  fontWeight: 600,
                }}
              >
                {t('about.status')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              {info.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3, md: 3.5 },
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(22, 28, 36, 0.8), rgba(30, 40, 52, 0.7))',
                      backdropFilter: 'blur(10px)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      maxWidth: '100%',
                      '&:hover': {
                        borderColor: index === 0
                          ? 'rgba(255, 107, 107, 0.4)'
                          : 'rgba(78, 205, 196, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: index === 0 ? '#ff6b6b' : '#4ecdc4',
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: index === 0 ? '#ff6b6b' : '#4ecdc4',
                        mb: 1.5,
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '1.125rem',
                        color: '#ffffff',
                        fontWeight: 700,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3.5,
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(22, 28, 36, 0.8), rgba(30, 40, 52, 0.7))',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d, #ff8787)',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em',
                      color: '#ffd93d',
                      mb: 2,
                      fontWeight: 700,
                    }}
                  >
                    {t('about.languagesTitle')}
                  </Typography>
                  <Stack spacing={1.5}>
                    {languages.map((lang, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: '8px',
                          background: `linear-gradient(135deg, ${lang.color}15, ${lang.color}08)`,
                          border: `1px solid ${lang.color}40`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${lang.color}25, ${lang.color}15)`,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 700,
                              color: lang.color,
                              minWidth: '28px',
                            }}
                          >
                            {lang.code}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: '#ffffff',
                              fontWeight: 600,
                            }}
                          >
                            {lang.name}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: lang.color,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                          }}
                        >
                          {lang.level}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default About;
